import React, { useRef, useState, useEffect, useMemo } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { MeshDistortMaterial } from "@react-three/drei";
import * as THREE from "three";
import { useAtom } from 'jotai';
import { videoStateAtom } from '../App';

const Video = ({
  position = [0, 0, 0],
  scale = 1,
  videoPath,
  scrollY = 0,
  sizes = { width: window.innerWidth, height: window.innerHeight },
  cursor = { x: 0, y: 0 },
  isVisible = true, // New prop to control visibility
  visibilityThreshold = 0.2, // Threshold for determining when to load/unload
  onStateChange = null, // Callback to notify parent of state changes
}) => {
  // Determine if device is mobile based on screen width
  const isMobile = useMemo(() => window.innerWidth <= 768, []);
  // Adjust position for mobile devices - move it further back
  const adjustedPosition = useMemo(() => {
    if (isMobile) {
      // Move the video further back on z-axis for mobile
      return [position[0], position[1], position[2] - 10.0];
    }
    return position;
  }, [position, isMobile]);
  
  // Get video state from global atom
  const [videoState, setVideoState] = useAtom(videoStateAtom);
  
  const meshRef = useRef();
  const materialRef = useRef();
  const videoRef = useRef();
  const textureRef = useRef();
  const [texture, setTexture] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isActive, setIsActive] = useState(true); // Track if component should be active
  const [textureReady, setTextureReady] = useState(false); // Track if texture is fully initialized
  const [isMuted, setIsMuted] = useState(true); // Track if audio is muted (default: muted)
  // Remove local hasUserInteracted state - now using global state
  const { gl, size, camera } = useThree();

  // No longer using the useMemo hook for placeholder texture
  // since we're loading it directly in the useEffect
  
  // Create a static texture first (blue color for desktop, placeholder for mobile)
  useEffect(() => {
    if (isMobile) {
      // For mobile, always try to use the placeholder texture first
      try {
        // Create a new texture loader
        const loader = new THREE.TextureLoader();
        // Load the texture directly in this effect
        loader.load(
          '/video/sizzle-reel-placeholder.jpg',
          // onLoad callback
          (loadedTexture) => {
            console.log("Placeholder texture loaded successfully in effect");
            loadedTexture.needsUpdate = true;
            setTexture(loadedTexture);
            setTextureReady(true); // Mark texture as ready immediately
          },
          // onProgress callback
          undefined,
          // onError callback
          (err) => {
            console.error("Error loading placeholder texture in effect:", err);
            // Create a fallback colored texture
            const fallbackTexture = new THREE.DataTexture(
              new Uint8Array([0, 0, 255, 255]), // RGBA (blue)
              1,
              1,
              THREE.RGBAFormat
            );
            fallbackTexture.needsUpdate = true;
            setTexture(fallbackTexture);
            setTextureReady(true); // Mark texture as ready immediately
          }
        );
      } catch (err) {
        console.error("Error creating placeholder texture in effect:", err);
        // Create a fallback colored texture
        const fallbackTexture = new THREE.DataTexture(
          new Uint8Array([0, 0, 255, 255]), // RGBA (blue)
          1,
          1,
          THREE.RGBAFormat
        );
        fallbackTexture.needsUpdate = true;
        setTexture(fallbackTexture);
        setTextureReady(true); // Mark texture as ready immediately
      }
    } else {
      // Create a fallback colored texture for desktop
      console.log("Using fallback blue texture for desktop");
      const fallbackTexture = new THREE.DataTexture(
        new Uint8Array([0, 0, 255, 255]), // RGBA (blue)
        1,
        1,
        THREE.RGBAFormat
      );
      fallbackTexture.needsUpdate = true;
      setTexture(fallbackTexture);
      setTextureReady(true); // Mark texture as ready immediately
    }
  }, [isMobile]);

  // Function to play video automatically - defined at the top level
  const playVideo = React.useCallback(() => {
    if (!videoRef.current || !isActive) {
      return;
    }

    try {
      // Ensure video is ready to play
      if (videoRef.current.readyState < 2) {
        videoRef.current.load();
      }

      // Make sure video is at the beginning if it ended
      if (videoRef.current.ended) {
        videoRef.current.currentTime = 0;
      }

      const playPromise = videoRef.current.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log("Video playback started automatically");
            setPlaying(true);
            
            // Update global state
            setVideoState(prev => ({
              ...prev,
              isPlaying: true,
              isVisible: true
            }));
          })
          .catch((error) => {
            console.error("Autoplay prevented:", error);
            // Force video to play by unmuting if that was the issue
            if (error.name === "NotAllowedError") {
              videoRef.current.muted = true;
              videoRef.current
                .play()
                .then(() => {
                  console.log("Video playback started automatically (muted)");
                  setPlaying(true);
                  
                  // Update global state
                  setVideoState(prev => ({
                    ...prev,
                    isPlaying: true,
                    isVisible: true,
                    isMuted: true
                  }));
                })
                .catch((err) => {
                  console.error("Still cannot play video:", err);
                });
            }
          });
      } else {
        // For browsers that don't return a promise
        setPlaying(true);
        
        // Update global state
        setVideoState(prev => ({
          ...prev,
          isPlaying: true,
          isVisible: true
        }));
      }
    } catch (err) {
      console.error("Error playing video:", err);
    }
  }, [isActive, setVideoState]);

  // Function to pause video - defined at the top level
  const pauseVideo = React.useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    try {
      // Save current playback position before pausing
      const currentTime = videoRef.current.currentTime;
      console.log("Saving playback position:", currentTime);
      
      videoRef.current.pause();
      console.log("Video paused");
      setPlaying(false);
      
      // Update global state with saved position
      setVideoState(prev => ({
        ...prev,
        isPlaying: false,
        savedPlaybackPosition: currentTime
      }));
    } catch (err) {
      console.error("Error pausing video:", err);
    }
  }, [setVideoState]);

  // Determine if the component should be active based on scroll position and visibility
  useEffect(() => {
    // If explicitly set to not visible, deactivate
    if (!isVisible) {
      setIsActive(false);
      // Pause video if it exists
      if (videoRef.current && playing) {
        pauseVideo();
      }
      
      // Update global state
      setVideoState(prev => ({
        ...prev,
        isVisible: false
      }));
      return;
    }

    // Check if user has started scrolling (scrollY > 0)
    const hasStartedScrolling = scrollY > 0;
    
    // Only proceed with activation if user has started scrolling
    if (!hasStartedScrolling) {
      setIsActive(false);
      
      // Update global state
      setVideoState(prev => ({
        ...prev,
        isVisible: false
      }));
      return;
    }

    // Calculate normalized scroll position
    const normalizedScroll =
      scrollY / (document.documentElement.scrollHeight - window.innerHeight);

    // Determine if component should be active based on scroll position
    // This logic can be adjusted based on when you want the video to be active
    const shouldBeActive =
      normalizedScroll < visibilityThreshold ||
      (normalizedScroll > 0.4 && normalizedScroll < 1.0);

    // Only update state if it's changing to avoid unnecessary re-renders
    if (shouldBeActive !== isActive) {
      console.log(
        `Video ${shouldBeActive ? "entering" : "leaving"} view, ${videoPath}`
      );
      setIsActive(shouldBeActive);
      
      // Update global state
      setVideoState(prev => ({
        ...prev,
        isVisible: shouldBeActive,
        videoPath: videoPath
      }));

      // If becoming active and video is loaded, play it
      if (shouldBeActive && videoLoaded && videoRef.current) {
        // Small delay to ensure everything is ready
        setTimeout(() => {
          if (videoRef.current) {
            playVideo();
          }
        }, 500);
      } else if (!shouldBeActive && videoLoaded && videoRef.current && playing) {
        // If leaving view and video is playing, pause it
        pauseVideo();
      }
    }
  }, [
    scrollY,
    isVisible,
    isActive,
    videoPath,
    visibilityThreshold,
    videoLoaded,
    playVideo,
    pauseVideo,
    playing,
    setVideoState
  ]);

  // Listen for external commands to toggle audio
  useEffect(() => {
    if (videoState.toggleAudio && videoRef.current) {
      // Toggle muted state
      const newMutedState = !isMuted;
      setIsMuted(newMutedState);
      
      // Apply to video element
      videoRef.current.muted = newMutedState;
      
      // Reset the toggle flag
      setVideoState(prev => ({
        ...prev,
        toggleAudio: false,
        isMuted: newMutedState
      }));
      
      console.log(`Audio ${newMutedState ? "muted" : "unmuted"} (external)`);
    }
  }, [videoState.toggleAudio, isMuted, setVideoState]);
  
  // Listen for external commands to play/pause
  useEffect(() => {
    if (videoState.togglePlayPause && videoRef.current) {
      console.log("External play/pause command received. isPlaying:", videoState.isPlaying);
      
      // hasUserInteracted is now set in App component's handlePlayPause
      
      if (videoState.isPlaying) {
        console.log("Playing video via external command");
        playVideo();
      } else {
        console.log("Pausing video via external command");
        pauseVideo();
      }
      
      // Reset the toggle flag
      setVideoState(prev => ({
        ...prev,
        togglePlayPause: false
      }));
    }
  }, [videoState.togglePlayPause, videoState.isPlaying, playVideo, pauseVideo, setVideoState]);
  
  // Listen for restart video command
  useEffect(() => {
    if (videoState.restartVideo && videoRef.current) {
      console.log("Restarting video");
      
      // Set the current time to 0 to restart the video
      videoRef.current.currentTime = 0;
      
      // Play the video
      playVideo();
      
      // Reset the restart flag
      setVideoState(prev => ({
        ...prev,
        restartVideo: false
      }));
    }
  }, [videoState.restartVideo, playVideo, setVideoState]);

  // No longer needed - removed

  // Create video element and texture manually instead of using useVideoTexture
  useEffect(() => {
    // Only initialize video if component is active and we have a path
    // For mobile, only initialize if it has been activated via external control
    if (!isActive || !videoPath || (isMobile && !videoState.mobileActivated)) {
      console.log(
        "Not initializing video: isActive=",
        isActive,
        "videoPath=",
        videoPath,
        "isMobile=",
        isMobile,
        "mobileActivated=",
        videoState.mobileActivated
      );
      return;
    }

    console.log("Initializing video with path:", videoPath);

    // Create video element
    const video = document.createElement("video");

    // Set common attributes
    video.src = videoPath;
    video.crossOrigin = "anonymous";
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.autoplay = false;
    video.preload = "auto"; // Ensure preloading

    // iOS specific attributes
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");

    // For older Android browsers
    video.setAttribute("x-webkit-airplay", "allow");

    // Add video to DOM temporarily to help with iOS/Safari
    video.style.display = "none";
    document.body.appendChild(video);

    // Store video reference
    videoRef.current = video;

    // Define event handlers
    const handleLoadedData = () => {
      console.log("Video data loaded");

      // Restore saved playback position if user has interacted
      if (videoState.hasUserInteracted && videoState.savedPlaybackPosition > 0) {
        console.log("Restoring playback position:", videoState.savedPlaybackPosition);
        video.currentTime = videoState.savedPlaybackPosition;
      }

      try {
        // Create texture from video with enhanced color settings
        const videoTexture = new THREE.VideoTexture(video);
        videoTexture.minFilter = THREE.LinearFilter;
        videoTexture.magFilter = THREE.LinearFilter;
        videoTexture.format = THREE.RGBFormat;

        // Ensure correct color space and encoding for vibrant colors
        // THREE.SRGBColorSpace is preferred for most video content
        videoTexture.colorSpace = THREE.SRGBColorSpace;

        // Set encoding to match the renderer's output encoding
        // This is critical for correct color reproduction
        videoTexture.encoding = THREE.sRGBEncoding;

        // Adjust for better color reproduction
        videoTexture.anisotropy = 16; // Higher anisotropy for sharper textures
        videoTexture.generateMipmaps = false;

        // Force multiple updates to ensure texture is properly initialized
        videoTexture.needsUpdate = true;

        // Schedule another update after a short delay to ensure proper initialization
        setTimeout(() => {
          if (videoTexture) {
            videoTexture.needsUpdate = true;
            console.log("Forced second texture update for better color");
          }
        }, 100);

        // Store texture reference but don't set state immediately
        textureRef.current = videoTexture;

        // Force multiple updates to ensure texture is properly initialized
        videoTexture.needsUpdate = true;

        // Delay setting the texture to state to ensure it's fully initialized
        setTimeout(() => {
          if (videoTexture) {
            // Force another update before setting to state
            videoTexture.needsUpdate = true;
            setTexture(videoTexture);
            setVideoLoaded(true);

            // Mark texture as ready after a short delay
            setTimeout(() => {
              setTextureReady(true);
              console.log("Texture marked as fully ready");
              
              // Update global state
              setVideoState(prev => ({
                ...prev,
                isLoaded: true
              }));
            }, 500);

            console.log(
              "Video texture created successfully with dimensions:",
              video.videoWidth,
              "x",
              video.videoHeight
            );
          }
        }, 100);
      } catch (err) {
        console.error("Error creating video texture:", err);
      }
    };
    
    // Handle duration change event
    const handleDurationChange = () => {
      if (videoRef.current && !isNaN(videoRef.current.duration)) {
        console.log("Video duration:", videoRef.current.duration);
        setVideoState(prev => ({
          ...prev,
          duration: videoRef.current.duration
        }));
      }
    };
    
    // Handle time update event
    const handleTimeUpdate = () => {
      if (videoRef.current) {
        setVideoState(prev => ({
          ...prev,
          currentTime: videoRef.current.currentTime
        }));
      }
    };

    const handleLoadedMetadata = () => {
      console.log("Video metadata loaded");
      
      // Restore saved playback position if user has interacted
      if (videoState.hasUserInteracted && videoState.savedPlaybackPosition > 0) {
        console.log("Restoring playback position from metadata:", videoState.savedPlaybackPosition);
        video.currentTime = videoState.savedPlaybackPosition;
      }
    };
    const handleCanPlay = () => console.log("Video can play");
    const handleCanPlayThrough = () => console.log("Video can play through");
    const handlePlay = () => console.log("Video play event");
    const handlePause = () => console.log("Video pause event");
    const handleError = (e) => {
      // Extract more useful information from the error event
      const errorDetails = e.target.error
        ? `Code: ${e.target.error.code}, Message: ${e.target.error.message}`
        : "Unknown error";
      console.error("Video error:", errorDetails);
    };
    const handleStalled = () =>
      console.warn("Video stalled - network issue may be occurring");
    const handleSuspend = () => {
      // This is normal browser behavior to conserve resources, only log if debugging
      if (process.env.NODE_ENV === "development") {
        console.log("Video loading suspended by browser (normal behavior)");
      }
    };
    const handleWaiting = () => console.warn("Video waiting for more data");

    // Add event listeners
    video.addEventListener("loadeddata", handleLoadedData);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("canplaythrough", handleCanPlayThrough);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("error", handleError);
    video.addEventListener("stalled", handleStalled);
    video.addEventListener("suspend", handleSuspend);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("durationchange", handleDurationChange);
    video.addEventListener("timeupdate", handleTimeUpdate);

    // Load video
    console.log("Loading video...");
    video.load();

    // More thorough cleanup
    return () => {
      console.log("Cleaning up video resources");
      if (videoRef.current) {
        // Save current position before cleanup if user has interacted
        if (videoState.hasUserInteracted && videoRef.current.currentTime > 0) {
          const currentTime = videoRef.current.currentTime;
          console.log("Saving position during cleanup:", currentTime);
          setVideoState(prev => ({
            ...prev,
            savedPlaybackPosition: currentTime
          }));
        }

        // Remove all event listeners to prevent memory leaks
        const videoElement = videoRef.current;
        videoElement.pause();

        // Remove all event listeners properly
        videoElement.removeEventListener("loadeddata", handleLoadedData);
        videoElement.removeEventListener(
          "loadedmetadata",
          handleLoadedMetadata
        );
        videoElement.removeEventListener("canplay", handleCanPlay);
        videoElement.removeEventListener(
          "canplaythrough",
          handleCanPlayThrough
        );
        videoElement.removeEventListener("play", handlePlay);
        videoElement.removeEventListener("pause", handlePause);
        videoElement.removeEventListener("error", handleError);
        videoElement.removeEventListener("stalled", handleStalled);
        videoElement.removeEventListener("suspend", handleSuspend);
        videoElement.removeEventListener("waiting", handleWaiting);
        videoElement.removeEventListener("durationchange", handleDurationChange);
        videoElement.removeEventListener("timeupdate", handleTimeUpdate);

        // Clear source and force browser to release resources
        videoElement.src = "";
        videoElement.load();

        // Remove from DOM if it was added
        if (videoElement.parentNode) {
          videoElement.parentNode.removeChild(videoElement);
        }

        videoRef.current = null;
      }

      if (textureRef.current) {
        textureRef.current.dispose();
        textureRef.current = null;
      }

      // Reset state when cleaning up
      setVideoLoaded(false);
      setPlaying(false);
      
      // Update global state
      setVideoState(prev => ({
        ...prev,
        isLoaded: false,
        isPlaying: false
      }));
    };
  }, [videoPath, isActive, isMobile, videoState.mobileActivated, videoState.hasUserInteracted, videoState.savedPlaybackPosition, setVideoState]);

  // Safe update function for animation frame
  const updateFrame = React.useCallback(
    (state, delta) => {
      // Always update texture if it exists and video exists, regardless of playing state
      // This ensures the texture shows the current frame even when paused
      if (isActive && textureRef.current && videoRef.current) {
        // Check if video is actually ready
        if (videoRef.current.readyState >= 2) {
          // HAVE_CURRENT_DATA or higher
          textureRef.current.needsUpdate = true;
        }
      }

      // Apply parallax and scroll effects to mesh position
      if (meshRef.current && cursor) {
        const time = state.clock.getElapsedTime();

        // Calculate scroll effect (moves toward camera as user scrolls)
        const scrollEffect = scrollY ? (scrollY / sizes.height) * 3 : 0;

        // Apply scroll effect to y-position (moves down slightly as user scrolls up)
        const baseY = adjustedPosition[1];
        const targetY =
          baseY + Math.sin(time * 0.3) * 0.05 + -scrollEffect * 0.2; // Add scroll effect to y-position with a smaller multiplier

        // Apply scroll effect to z-position
        const baseZ = adjustedPosition[2];
        const targetZ = baseZ + scrollEffect * 0.4; // Add scroll effect to base z-position

        // Add parallax effect for x-position
        const parallaxFactor = isMobile ? 0.15 : 0.3; // Reduce parallax effect on mobile
        const targetX = adjustedPosition[0] - cursor.x * parallaxFactor;

        // Apply position changes with smooth easing
        meshRef.current.position.x +=
          (targetX - meshRef.current.position.x) * 0.08;
        meshRef.current.position.y +=
          (targetY - meshRef.current.position.y) * 0.1; // Apply smooth easing to y-position
        meshRef.current.position.z +=
          (targetZ - meshRef.current.position.z) * 0.15; // Increased easing factor for faster z transition
      }

      // Animate material distortion with wave effect
      if (materialRef.current) {
        const time = state.clock.getElapsedTime();

        // Base distortion value
        const baseDistort = 0.3;

        // Animated distortion with sine wave
        const animatedDistort = baseDistort + Math.sin(time) * 0.05;

        // Apply distortion
        materialRef.current.distort = animatedDistort;
      }
    },
    [isActive, cursor, scrollY, adjustedPosition, sizes.height, isMobile]
  );

  // Update texture on each frame when playing and apply parallax effects
  useFrame(updateFrame);

  // Calculate aspect ratio to maintain video proportions (16:9)
  const videoScale = [(scale * 16) / 9, scale, scale];

  // Initial auto-play when video is loaded (only if user hasn't interacted)
  useEffect(() => {
    if (isActive && videoLoaded && videoRef.current && !videoState.hasUserInteracted) {
      console.log("Initial auto-play effect triggered");
      
      // Check if the video is actually in a state where it can play
      if (videoRef.current.readyState >= 2) {
        // HAVE_CURRENT_DATA or higher
        const timer = setTimeout(() => {
          playVideo();
        }, 1000);

        return () => clearTimeout(timer);
      } else {
        // If not ready yet, set up a one-time event listener
        const handleCanPlay = () => {
          setTimeout(() => playVideo(), 1000);
          videoRef.current.removeEventListener("canplay", handleCanPlay);
        };
        videoRef.current.addEventListener("canplay", handleCanPlay);

        return () => {
          if (videoRef.current) {
            videoRef.current.removeEventListener("canplay", handleCanPlay);
          }
        };
      }
    }
  }, [videoLoaded, isActive, playVideo, videoState.hasUserInteracted]);

  // If component is not active, return an empty group instead of null
  // This keeps the component mounted but invisible
  if (!isActive) {
    return <group visible={false} />;
  }

  return (
    <group visible={isActive}>
      <mesh
        ref={meshRef}
        position={adjustedPosition}
        scale={videoScale}
        renderOrder={4}
      >
        {/* Simple plane with minimal segments */}
        <planeGeometry args={[1, 1, 32, 32]} />

        {/* Enhanced material with more vibrant colors and distortion effect */}
        {texture && textureReady && (
          <MeshDistortMaterial
            ref={materialRef}
            map={texture}
            toneMapped={false}
            transparent={false}
            opacity={1.0}
            distort={0.3}
            speed={1.5}
            radius={1}
            envMapIntensity={0.0} // Disable environment map influence
            metalness={0.0} // No metalness for accurate video colors
            roughness={1.0} // Full roughness for accurate video colors
            color={0xffffff} // Ensure base color is white for accurate video colors
          />
        )}
      </mesh>
    </group>
  );
};

export default Video;
